/**
 * /execution/promotion-queue — read-only vetting candidates list.
 *
 * Surfaces contractors whose `vettingStatus === "trial"` and shows what's
 * blocking promotion against the HARD GATE (license / insurance / photos /
 * skill checklist / jobs / score thresholds).
 *
 * No writers — promotion happens via the canonical contractor profile flow.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import { computeExecutionScore } from "@/data/contractor/networkExecution/executionScore";
import { VETTING_PROMOTION_REQUIREMENTS } from "@/data/contractor/networkExecution/vettingPromotion";

export default function ExecutionPromotionQueuePage() {
  const { role } = useRole();
  const { contractors, jobs, callbacks } = useAppData();

  const candidates = useMemo(() => {
    return contractors
      .filter((c) => c.vettingStatus === "trial")
      .map((c) => {
        const v = c.vetting;
        const photos = v?.submittedJobPhotos.length ?? 0;
        const score = computeExecutionScore({ contractor: c, jobs, callbacks }).score;
        const checks = {
          license: v?.licenseVerified === true,
          insurance: v?.insuranceVerified === true,
          photos: photos >= VETTING_PROMOTION_REQUIREMENTS.minPhotos,
          skill: v?.skillChecklistCompleted === true,
          jobs: (c.jobsCompleted ?? 0) >= VETTING_PROMOTION_REQUIREMENTS.minJobsCompleted,
          score: score !== null && score >= VETTING_PROMOTION_REQUIREMENTS.minScore,
        };
        const passed = Object.values(checks).filter(Boolean).length;
        const total = Object.keys(checks).length;
        return { contractor: c, photos, score, checks, passed, total, ready: passed === total };
      })
      .sort((a, b) => b.passed - a.passed);
  }, [contractors, jobs, callbacks]);

  if (!canAccessExecutionDomain(role)) return null;

  return (
    <CockpitPage
      
      title="Promotion Queue"
      maxWidth="7xl"
    >
      {candidates.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No contractors are in trial. The promotion queue is empty.
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Contractor</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>License</TableHead>
                <TableHead>Insurance</TableHead>
                <TableHead>Photos</TableHead>
                <TableHead>Skill</TableHead>
                <TableHead>Jobs</TableHead>
                <TableHead>Score</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {candidates.map(({ contractor: c, photos, score, checks, passed, total, ready }) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link to={`/execution/vetting/${c.id}`} className="text-[12px] font-medium hover:underline text-foreground">
                      {c.companyName}
                    </Link>
                  </TableCell>
                  <TableCell className="text-[11px] tabular-nums">{passed} / {total}</TableCell>
                  <Cell ok={checks.license}>{checks.license ? "✓" : "—"}</Cell>
                  <Cell ok={checks.insurance}>{checks.insurance ? "✓" : "—"}</Cell>
                  <Cell ok={checks.photos}>{photos} / {VETTING_PROMOTION_REQUIREMENTS.minPhotos}</Cell>
                  <Cell ok={checks.skill}>{checks.skill ? "✓" : "—"}</Cell>
                  <Cell ok={checks.jobs}>{c.jobsCompleted ?? 0} / {VETTING_PROMOTION_REQUIREMENTS.minJobsCompleted}</Cell>
                  <Cell ok={checks.score}>{score === null ? "—" : score}</Cell>
                  <TableCell className="text-right">
                    {ready ? (
                      <Badge className="bg-success/15 text-success border-success/30">Ready to promote</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">In progress</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <div className="mt-4">
        <Button asChild variant="outline" size="sm">
          <Link to="/contractors">Open Contractors workspace</Link>
        </Button>
      </div>
    </CockpitPage>
  );
}

function Cell({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <TableCell className={`text-[11px] tabular-nums ${ok ? "text-success font-semibold" : "text-muted-foreground"}`}>
      {children}
    </TableCell>
  );
}
